import { Button, Input, Table } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useCreateMiscExpense } from "../../api/api";
import { useGetMiscExpenses } from "../../api/api.get";
import DateComponent from "../../components/date.component";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IExpense2 } from "../../types/types";
import { toFixedDisplay } from "../../utils/number.utils";

export default function MiscExpensesPage() {
  const { defaultGroupId } = useGroupContext();

  const { mutateAsync } = useCreateMiscExpense(defaultGroupId);
  const { currency } = useCurrencyContext();
  const [date, setDate] = useState<dayjs.Dayjs>(dayjs());

  const {
    data: miscExpensesData,
    isLoading: isExpenseListLoading,
    refetch,
  } = useGetMiscExpenses(
    defaultGroupId,
    date.startOf("M").format("YYYY-MM-DD"),
    date.endOf("M").format("YYYY-MM-DD"),
  );

  const { notification, notifyError, notifySuccess } = useNotifications();

  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: "",
      amount: "",
      date: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
      amount: Yup.number().required("Please enter name"),
      date: Yup.string().required("Please select date"),
    }),
    onSubmit: async (values) => {
      if (!defaultGroupId) {
        notifyError("Please set a group.");
        return;
      }
      mutateAsync({
        comments: values.name,
        expenseDate: values.date,
        amount: +values.amount,
        type: "misc",
      })
        .then(async () => {
          await refetch();
          notifySuccess({ description: "Misc expense successfully created" });
          formik.resetForm();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  const handleDateChange = (date: dayjs.Dayjs) => {
    setDate(date);
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "comments",
      key: "comments",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Amount",
      key: "amount",
      render: (item: IExpense2) => (
        <Link
          to={`/groups/${item.groupId}/expenses/${item.id}`}
          className="font-bold text-blue-500"
        >
          {currency}
          {toFixedDisplay(item.amount)}
        </Link>
      ),
      sorter: (a: IExpense2, b: IExpense2) => a.amount - b.amount,
      sortDirections: ["descend", "ascend"] as any[],
    },
    {
      title: "Date",
      dataIndex: "expensedate",
      key: "expensedate",
      render: (date: string) => (
        <span>{dayjs(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "",
      key: "options",
      width: 30,
      render: (_: any, record: IExpense2) => (
        <Button
          type="link"
          onClick={() =>
            navigate(`/groups/${record.groupId}/expenses/${record.id}`)
          }
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              One Time Expense
            </h1>
            <p className="text-muted-foreground text-gray-500 text-sm">
              Track your one time expenses separately apart from regular monthly
              expenses. For eg. car repair.
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DateComponent
                      onChange={(date) => handleDateChange(date)}
                    />
                  </CardTitle>
                  <CardDescription className="mx-10">
                    {miscExpensesData?.length} Expense
                    {miscExpensesData?.length !== 1 ? "s" : ""}{" "}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Table
                      dataSource={miscExpensesData}
                      columns={columns}
                      loading={isExpenseListLoading}
                      pagination={{ pageSize: 50 }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel: Add Account Form */}
            <div className="flex flex-col gap-8">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add One Time Expense</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Description *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Summer Vacation"
                        {...formik.getFieldProps("name")}
                        required
                      />
                      {!!formik.errors.name && (
                        <p className="text-red-500 text-xs">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>

                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="text-xs font-semibold text-foreground mb-1">
                        Amount *
                      </label>
                      <div className="relative">
                        <Input
                          type="text"
                          placeholder="e.g., 150.00"
                          {...formik.getFieldProps("amount")}
                          required
                        />
                        {!!formik.errors.amount && (
                          <p className="text-red-500 text-xs">
                            {formik.errors.amount}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="sm:col-span-1 lg:col-span-1">
                      <label className="text-xs font-semibold text-foreground mb-1">
                        Date *
                      </label>
                      <Input
                        type="date"
                        value={formik.values.date}
                        onChange={(e) =>
                          formik.setFieldValue("date", e.target.value)
                        }
                        className="text-sm"
                      />
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => formik.resetForm()}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
